import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

/* ===================== Server-time helpers ===================== */
const OFFSET_KEY = "serverOffsetMs_v1";

// Get server time by reading the Date header from a same-origin GET
async function fetchServerNowUtc() {
  try {
    const res = await fetch(`${window.location.origin}/`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h); // GMT
  } catch {}
  try {
    const res = await fetch(`${window.location.origin}/favicon.ico`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}
  return new Date(); // fallback (only if server unreachable)
}

// Initialize (or reuse) an offset so: serverNow = new Date(Date.now() + offsetMs)
async function ensureServerOffset() {
  const cached = sessionStorage.getItem(OFFSET_KEY);
  if (cached != null && !Number.isNaN(Number(cached))) {
    return Number(cached);
  }
  const serverNow = await fetchServerNowUtc();
  const offset = serverNow.getTime() - Date.now();
  sessionStorage.setItem(OFFSET_KEY, String(offset));
  return offset;
}

// Accessor that always gives “business now” from the server offset
function businessNow() {
  const raw = sessionStorage.getItem(OFFSET_KEY);
  const offset = raw == null ? 0 : Number(raw);
  return new Date(Date.now() + (Number.isFinite(offset) ? offset : 0));
}
/* =============================================================== */

// --- inactive helpers ---
const hasValue = (v) =>
  v !== null &&
  v !== undefined &&
  String(v).trim() !== "" &&
  String(v).toLowerCase() !== "null" &&
  String(v).toLowerCase() !== "undefined";

const isTrueLike = (v) =>
  v === true || Number(v) === 1 || String(v).toLowerCase() === "true";

const isFalseLike = (v) =>
  v === false || Number(v) === 0 || String(v).toLowerCase() === "false";

const Renew = () => {
  useEscapeToGoBack();
  const navigate = useNavigate();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  const [error, setError] = useState("");
  const [amountCollected, setAmountCollected] = useState("");
  const [serverReady, setServerReady] = useState(false); // ensure we have offset before strict checks

  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);
  const onlyMoney = (s = "") => (s || "").replace(/[^\d.]/g, "");

  // One-time: ensure we have server offset
  useEffect(() => {
    let alive = true;
    (async () => {
      await ensureServerOffset();
      if (alive) setServerReady(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Autofocus MMYY on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      mmyyRef.current?.focus();
      mmyyRef.current?.select?.();
    });
  }, []);

  // -------- helpers --------
  const toMoney = (n) => Number(n ?? 0).toFixed(2);

  const extractYmd = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: +m[1], m: +m[2], d: +m[3] };
  };

  const computeMonthsPassed = (grantDateStr, refDate = businessNow()) => {
    const ymd = extractYmd(grantDateStr);
    if (!ymd) return 1;
    const g = new Date(ymd.y, ymd.m - 1, ymd.d);
    const t = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
    let months = (t.getFullYear() - g.getFullYear()) * 12 + (t.getMonth() - g.getMonth());
    if (t.getDate() > g.getDate()) months += 1;
    if (months < 1) months = 1;
    return Math.min(8, months);
  };

  // Interest Calculation (Memoized function)
  // This updates whenever transaction or firstMonthDiscount changes.
  const calculateInterest = useMemo(() => {
    return () => {
      if (!transaction) return "0.00";
      const principal = Number(transaction.amount);
      if (!Number.isFinite(principal) || principal <= 0 || !extractYmd(transaction.grantDate)) return "";
      const monthsPassed = computeMonthsPassed(transaction.grantDate);
      let totalRate = 0.015 * monthsPassed;
      if (firstMonthDiscount) totalRate -= 0.005;
      if (totalRate < 0) totalRate = 0;
      return toMoney(principal * totalRate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, firstMonthDiscount, serverReady]);

  /* =========================================================================
     LOGIC UPDATES START HERE
  ========================================================================= */

  // 1. Set Checkbox Default State
  // Runs ONLY when a new transaction is loaded.
  useEffect(() => {
    if (!transaction) {
      setFirstMonthDiscount(false);
      return;
    }
    const months = computeMonthsPassed(transaction.grantDate);
    // If 1 month, default to checked. Otherwise unchecked.
    setFirstMonthDiscount(months === 1);
  }, [transaction]);

  // 2. Sync Amount Collected with Calculated Interest
  // Runs when Transaction loads OR when Checkbox is toggled.
  // This satisfies "if they uncheck the box... it follows".
  useEffect(() => {
    // If the calculateInterest function changed (meaning inputs changed),
    // update the textbox.
    setAmountCollected(calculateInterest());
  }, [calculateInterest]); 

  // Note: Since 'setAmountCollected' is NOT called in the input's onChange below,
  // manual typing will NOT trigger this effect. This satisfies "ammend also need to be made possible".

  /* =========================================================================
     LOGIC UPDATES END HERE
  ========================================================================= */

  // --- derived: is ticket inactive/redeemed? ---
  const isInactive = useMemo(() => {
    if (!transaction) return false;
    return (
      isFalseLike(transaction.activeRecord) ||
      hasValue(transaction.redeemDate) ||
      (hasValue(transaction.redeemAmount) && Number(transaction.redeemAmount) > 0) ||
      hasValue(transaction.redeemBy) ||
      isTrueLike(transaction.transactionStatus)
    );
  }, [transaction]);

  const handleNext = () => {
    if (!transaction) {
      alert("Please search and select a valid pawn ticket first.");
      return;
    }

    if (isInactive) {
      alert("Ticket is inactive and cannot be renewed.");
      return;
    }

    if (!serverReady) { alert("Please wait for the server clock before renewing."); return; }
    if (!extractYmd(transaction.grantDate) || !Number.isFinite(Number(transaction.amount)) || Number(transaction.amount) <= 0) {
      alert("Ticket grant date or principal is missing/invalid. Interest cannot be calculated. Please check the ticket before renewing.");
      return;
    }
    const raw = onlyMoney(amountCollected);
    const amtNum = Number(raw);
    if (!raw || !/^\d+(?:\.\d{1,2})?$/.test(raw) || !Number.isFinite(amtNum) || amtNum < 0) {
      alert("Interest / amount collected cannot be blank or invalid. Please enter and verify the amount before continuing.");
      return;
    }

    const expectedInterest = Number(calculateInterest());
    const toCents = (n) => Math.round(n * 100);

    const issues = [];
    if (amtNum === 0) {
      issues.push("FREE RENEWAL: Confirm the shop has waived all interest. A $0.00 renewal payment record will still be created.");
    }
    if (Math.abs(toCents(amtNum) - toCents(expectedInterest)) >= 1) {
      issues.push(
        `Entered interest ($${amtNum.toFixed(2)}) ≠ expected ($${expectedInterest.toFixed(2)}).`
      );
    }

    if (issues.length) {
      const proceed = window.confirm(
        `Please confirm:\n\n${issues.map((s) => `• ${s}`).join("\n")}\n\n` +
          `Principal: $${Number(transaction.amount || 0).toFixed(2)}\n` +
          `Months: ${computeMonthsPassed(transaction.grantDate)}${
            firstMonthDiscount ? " (−0.5% discount applied)" : ""
          }\n\n` +
          `Continue anyway?`
      );
      if (!proceed) return;
    }

    navigate("/renew-grant", {
      state: {
        items: String(transaction.item || "").split(", "),
        valuation: transaction.valuationAmount,
        weight: transaction.weight,
        principal: transaction.amount,
        remark: transaction.remark,
        grantDate: transaction.grantDate,
        expiryDate: transaction.expiryDate,
        oldReceiptNo: transaction.receiptNo,
        amountCollected: amtNum.toFixed(2),
      },
    });
  };

  const handleSearch = async () => {
    setTransaction(null);
    setAmountCollected("");
    try {
      const { data } = await axios.get(
        `https://www.lianfoong.com/api/pawn-transactions/search`,
        { params: { mmyy, receiptNo } }
      );
      setTransaction(data || null);
      setError(data ? "" : "Transaction not found!");
    } catch {
      setTransaction(null);
      setError("Transaction not found!");
    }
  };

  const formatDateDDMMYYYY = (dateString) => {
    const ymd = extractYmd(dateString);
    if (!ymd) return "";
    const day = String(ymd.d).padStart(2, "0");
    const month = String(ymd.m).padStart(2, "0");
    const year = ymd.y;
    return `${day}/${month}/${year}`;
  };

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
      receiptRef.current?.select?.();
    }
  };

  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (transaction && !isInactive) {
        handleNext();
      } else {
        handleSearch();
      }
    }
  };

  return (
    <div className="renew-container">
      <h2 className="title">Renew Pawn Ticket</h2>

      {!serverReady && (
        <p style={{ color: "#555", marginBottom: 8 }}>
          Loading server time… You can still search, calculations will sync in a moment.
        </p>
      )}

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          type="text"
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(e.target.value)}
          onKeyDown={onMmyyKeyDown}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          type="text"
          ref={receiptRef}
          value={receiptNo}
          onChange={(e) => setReceiptNo(e.target.value)}
          onKeyDown={onReceiptKeyDown}
        />
      </div>

      <button className="search-btn" onClick={handleSearch}>Search</button>

      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount).toFixed(2)}</p>
            <p><strong>Month Pass:</strong> {computeMonthsPassed(transaction.grantDate)}</p>
            <p><strong>Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="discount"
              checked={firstMonthDiscount}
              onChange={(e) => setFirstMonthDiscount(e.target.checked)}
              disabled={isInactive}
            />
            <label htmlFor="discount">First Month Interest Discount (-0.5% total)</label>
          </div>

          <div className="details-grid">
            <p><strong>Interest:</strong> ${calculateInterest()}</p>
            <p><strong>Amount + Interest:</strong> {
              (() => {
                const interest = Number(calculateInterest());
                return toMoney(Number(transaction.amount || 0) + interest);
              })()
            }</p>
          </div>

          <div className="form-group">
            <label>Amount Collected :</label>
            <input
              type="text"
              value={amountCollected}
              onChange={(e) => setAmountCollected(onlyMoney(e.target.value))}
              disabled={isInactive}
            />
          </div>

          {isInactive && (
            <p className="error-message" style={{ marginTop: 12 }}>
              Ticket is inactive.
            </p>
          )}

          <button
            className="next-btn"
            onClick={handleNext}
            disabled={isInactive}
            title={isInactive ? "Ticket is inactive and cannot be renewed." : "Go to renew grant"}
            aria-disabled={isInactive ? "true" : "false"}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Renew;